/**
 * API client for communicating with the backend
 */

/** Strip trailing slash and accidental `/api` so we don’t call `/api/api/...`. */
function normalizeApiBase(url: string): string {
  let u = url.trim().replace(/\/$/, '');
  u = u.replace(/\/api\/?$/i, '');
  return u;
}

export const API_URL = normalizeApiBase(
  import.meta.env.VITE_API_URL || 'http://localhost:3001'
);

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
  /** If true, returns null instead of throwing on 401 (useful for auth checks) */
  silent401?: boolean;
}

/** Avoid dumping full HTML error pages (e.g. Express "Cannot GET …") into UI or logs as one giant string. */
function messageFromErrorBody(status: number, statusText: string, body: string): string {
  const trimmed = body.trim();
  if (trimmed.startsWith("<!DOCTYPE") || trimmed.toLowerCase().includes("<html")) {
    const pre = trimmed.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i);
    if (pre?.[1]) {
      const line = pre[1].trim().split("\n")[0] || pre[1].trim();
      return line.length > 200 ? `${line.slice(0, 200)}…` : `${line} (HTTP ${status})`;
    }
    return `Server returned an HTML error page (HTTP ${status}). Check VITE_API_URL matches your latest backend, or redeploy the API.`;
  }
  try {
    const parsed = JSON.parse(trimmed) as { error?: string; message?: string };
    return parsed.error || parsed.message || `HTTP ${status}: ${statusText}`;
  } catch {
    const text = trimmed.replace(/\s+/g, " ");
    return text.length > 240 ? `${text.slice(0, 240)}…` : text || `HTTP ${status}: ${statusText}`;
  }
}

export async function api<T = unknown>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const { params, silent401, ...fetchOptions } = options;

  let url = `${API_URL}/api${endpoint}`;
  
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = fetchOptions.body instanceof FormData;
  const headers: HeadersInit = isFormData
    ? {}
    : { 'Content-Type': 'application/json' };

  // Add Authorization header if token exists (fallback for cross-origin)
  const token = localStorage.getItem('token');
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include',
      headers: {
        ...headers,
        ...fetchOptions.headers,
      },
    });
  } catch (fetchError: any) {
    console.error('Network error:', fetchError?.message || fetchError, { url: API_URL });
    throw new Error(`Failed to connect to backend at ${API_URL}. Make sure the backend server is running on port 3001.`);
  }

  if (!response.ok) {
    // Silently return null for 401 when silent401 is set (auth checks)
    if (silent401 && response.status === 401) {
      return null as T;
    }
    const errorText = await response.text();
    const message = messageFromErrorBody(
      response.status,
      response.statusText,
      errorText
    );
    console.error("API error:", message, response.status, url);
    throw new Error(message);
  }

  return response.json();
}

export const apiGet = <T = unknown>(endpoint: string, params?: Record<string, string>) =>
  api<T>(endpoint, { method: 'GET', params });

export const apiPost = <T = unknown>(endpoint: string, body?: unknown) =>
  api<T>(endpoint, { method: 'POST', body: JSON.stringify(body) });

export const apiPatch = <T = unknown>(endpoint: string, body?: unknown) =>
  api<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) });

export const apiDelete = <T = unknown>(endpoint: string) =>
  api<T>(endpoint, { method: 'DELETE' });
